import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { Popover } from '@mantine/core';
import styles from './styles.scoped.css';

function buildLabel(value = [], data = [], placeholder = '') {
  if (!value.length) {
    return placeholder;
  }

  const labelMap = data.reduce((accumulator, item) => {
    accumulator[item.value] = item.label;
    return accumulator;
  }, {});
  const selectedLabels = value.map((item) => labelMap[item] || item);

  if (selectedLabels.length === 1) {
    return selectedLabels[0];
  }

  return `${selectedLabels[0]}, dan ${selectedLabels.length - 1} lainnya`;
}

export default function AuditFilterSelect({
  data = [],
  hasMore = false,
  label = '',
  loading = false,
  name = '',
  onChange = () => {},
  onFetchMore = () => {},
  onOpen = () => {},
  onSearchChange = () => {},
  placeholder = '',
  searchPlaceholder = '',
  value = [],
}) {
  const [opened, setOpened] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const normalizedValue = useMemo(() => value || [], [value]);
  const displayValue = useMemo(
    () => buildLabel(normalizedValue, data, placeholder),
    [normalizedValue, data, placeholder]
  );

  const handleToggleValue = (nextValue) => {
    if (normalizedValue.includes(nextValue)) {
      onChange(normalizedValue.filter((item) => item !== nextValue));
      return;
    }

    onChange([...normalizedValue, nextValue]);
  };

  const handleClose = () => {
    setOpened(false);
    setSearchValue('');
  };

  const handleOpen = () => {
    if (!opened) {
      setOpened(true);
      onOpen(name);
      return;
    }

    handleClose();
  };

  const handleScroll = (event) => {
    if (loading || !hasMore) {
      return;
    }

    const { scrollHeight, scrollTop, clientHeight } = event.currentTarget;
    if (scrollHeight - scrollTop - clientHeight <= 24) {
      onFetchMore(searchValue, name);
    }
  };

  return (
    <Popover
      onChange={(nextOpened) => {
        if (!nextOpened) {
          handleClose();
          return;
        }

        setOpened(nextOpened);
      }}
      opened={opened}
      position="bottom-start"
      shadow="md"
      width="target"
      withArrow={false}
      withinPortal
      zIndex={1000}
    >
      <Popover.Target>
        <button
          className={styles.auditFilterTrigger}
          onClick={handleOpen}
          type="button"
        >
          <span className={styles.auditFilterText}>
            <span className={styles.auditFilterLabel}>{label}</span>
            <span
              className={[
                styles.auditFilterValue,
                normalizedValue.length ? styles.auditFilterValueSelected : '',
              ].filter(Boolean).join(' ')}
            >
              {displayValue}
            </span>
          </span>
          <span className={styles.auditFilterIcon}>
            <img alt="" aria-hidden="true" src="assets/ic-arrow-updown.svg" />
          </span>
        </button>
      </Popover.Target>
      <Popover.Dropdown className={styles.auditFilterDropdown}>
        <div className={styles.auditFilterSearchWrapper}>
          <img alt="" aria-hidden="true" src="assets/ic-search.svg" />
          <input
            className={styles.auditFilterSearchInput}
            onChange={(event) => {
              const nextValue = event.currentTarget.value;
              setSearchValue(nextValue);
              onSearchChange(nextValue, name);
            }}
            placeholder={searchPlaceholder}
            value={searchValue}
          />
          {searchValue && (
            <button
              className={styles.auditFilterClear}
              onClick={() => {
                setSearchValue('');
                onSearchChange('', name);
              }}
              type="button"
            >
              ×
            </button>
          )}
        </div>
        <div className={styles.auditFilterOptions} onScroll={handleScroll}>
          {data.length ? data.map((item) => {
            const checked = normalizedValue.includes(item.value);

            return (
              <button
                className={[
                  styles.auditFilterOption,
                  checked ? styles.auditFilterOptionSelected : '',
                ].filter(Boolean).join(' ')}
                key={item.value}
                onClick={() => handleToggleValue(item.value)}
                type="button"
              >
                <span
                  className={[
                    styles.auditFilterCheckbox,
                    checked ? styles.auditFilterCheckboxChecked : '',
                  ].filter(Boolean).join(' ')}
                >
                  {checked && <span className={styles.auditFilterCheckIcon} />}
                </span>
                <span className={styles.auditFilterOptionLabel}>{item.label}</span>
              </button>
            );
          }) : (
            <div className={styles.auditFilterEmpty}>Tidak Ada Data</div>
          )}
          {loading && (
            <div className={styles.auditFilterLoading}>Memuat...</div>
          )}
        </div>
      </Popover.Dropdown>
    </Popover>
  );
}

AuditFilterSelect.propTypes = {
  data: PropTypes.array,
  hasMore: PropTypes.bool,
  label: PropTypes.string,
  loading: PropTypes.bool,
  name: PropTypes.string,
  onChange: PropTypes.func,
  onFetchMore: PropTypes.func,
  onOpen: PropTypes.func,
  onSearchChange: PropTypes.func,
  placeholder: PropTypes.string,
  searchPlaceholder: PropTypes.string,
  value: PropTypes.array,
};
